import { cloneDeep, flatten } from 'lodash-es';
import {
  Finishing,
  LaminationType,
  LaminationWeight,
  PageSize,
  PagesPerSheet,
  PageTurn,
  PaperWeight,
  PrintColor,
  PrintSettings,
  Settings
} from '../../core/settings';
import { DiscountType, Price } from '../../global/models';
import {
  _PrintFile,
  CoverTypeConfig,
  DocumentListUI,
  ExtraPrices,
  HardCoverPrice,
  PrintingGroup,
  PrintVariant,
  Product,
  ProductBase,
  ProductPrint,
  ProductSnapshot,
  ProductSubType,
  ProductType,
  ProductUI,
  ProductVariant,
  ProductWithVariants,
  ProductWithVariantsUI,
  RingColorPrice,
  VendorRef,
  WeightMatrix
} from './product.models';

// ─── Basic product checks ────────────────────────────────────────────────────

/**
 * Determines whether an item is sold out.
 * @param item - Object with stock count and back-order permission flag.
 * @returns `true` when the item has no stock and does not allow back-orders.
 */
export const isSoldOut = (item: { allowSaleNoStock: boolean; stock: number; }): boolean => {
  return !item?.allowSaleNoStock && item?.stock <= 0;
};

/**
 * Determines whether every variant — or the product itself when variant-less — is sold out.
 * @param product - The product to evaluate.
 * @returns `true` when all variants (or the base product if there are none) are sold out.
 */
export const isAllProductSoldOut = (product: Product | ProductWithVariants | ProductWithVariantsUI): boolean => {
  return !product.variantCount
    ? isSoldOut(product)
    : (product.variants as ProductVariant[]).every(isSoldOut);
};

/**
 * Checks whether a product belongs to the "custom" store sub-type.
 * @param product - The product to evaluate.
 * @returns `true` when the product's `subType` equals {@link ProductSubType.CUSTOM}.
 */
export const isCustom = (product: Product): boolean => {
  return (product as any).subType === ProductSubType.CUSTOM;
};

// ─── Price helpers ───────────────────────────────────────────────────────────

/**
 * Calculates the unit price after applying a percentage discount.
 * @param item - Object with a base `unitPrice` and a `discount` percentage (0–100).
 * @returns Discounted {@link Price} when a discount is set; otherwise the original unit price.
 */
export const saledPrice = (item: { unitPrice: Price; discount: number; }): Price => {
  return item?.discount
    ? { amt: item.unitPrice.amt * (1 - item.discount / 100), cur: item.unitPrice.cur }
    : item?.unitPrice;
};

/**
 * Returns the final displayed price for a print product, including extras and creator royalty.
 * @param product - A print product with `unitPrice`, optional `extra.price` and optional `royalty`.
 * @returns {@link Price} composed of base amount + extra finishing price + royalty.
 */
export const printSaledPrice = (product: Product): Price => {
  const extraPrice = product.extra?.price ?? 0;
  const royalty = (product as any).royalty ?? 0;
  return {
    amt: product.unitPrice.amt + extraPrice + royalty,
    cur: product.unitPrice.cur,
  };
};

/**
 * Returns the unit price for a print product before adding the creator royalty.
 * @param product - A print product with `unitPrice` and optional `extra.price`.
 * @returns {@link Price} composed of base amount + extra finishing price (royalty excluded).
 */
export const printUnitPrice = (product: Product): Price => {
  const extraPrice = product.extra?.price ?? 0;
  return {
    amt: product.unitPrice.amt + extraPrice,
    cur: product.unitPrice.cur,
  };
};

// ─── Variant helpers ─────────────────────────────────────────────────────────

/**
 * Finds the variant with the lowest sale price.
 * @param variants - Array of product variants to compare.
 * @returns The cheapest {@link ProductVariant} by sale price, or `null` if the array is empty.
 */
export const cheapestVariant = (variants: Array<ProductVariant>): ProductVariant | null => {
  return variants.length
    ? variants.reduce((v, variant) => {
      return v && saledPrice(variant).amt > saledPrice(v).amt ? v : variant;
    })
    : null;
};

/**
 * Computes the maximum discount percentage across all provided variants.
 * @param variants - Array of product variants to compare.
 * @returns The highest discount value found, `0` when no variant has a discount,
 *          or `null` if the array is empty.
 */
export const highestDiscount = (variants: Array<ProductVariant>): number | null => {
  return variants.length
    ? variants.reduce((maxDiscount, currentVariant) => {
      return currentVariant.discount > maxDiscount ? currentVariant.discount : maxDiscount;
    }, 0)
    : null;
};

/**
 * Calculates the minimum possible total price for a custom product by finding
 * the cheapest available paper-format × sheet-number combination.
 * @param product - A custom store product with `customElements` configuration.
 * @returns The cheapest total price (base + combination), or `null` when the product
 *          is not custom or lacks the required element definitions.
 */
export function cheapestCombinationPrice(product: Product): number | null {
  if (!isCustom(product)) return null;
  const customElements = (product as any).customElements;
  if (!customElements) return null;

  const paperFormat = customElements.find(c => c.id === 'paperFormat');
  const sheetNumber = customElements.find(c => c.id === 'sheetNumber');
  if (!paperFormat || !sheetNumber) return null;

  const prices = flatten<number>(
    paperFormat.values.map(p =>
      p.available
        ? p.priceBySheetNumber.map(e =>
          sheetNumber.values.find(s => s.value === e.sheetNumber)?.available
            ? e.price * e.sheetNumber
            : 0,
        )
        : [],
    ),
  ).filter(p => !!p);

  const minPrice = Math.min(...prices);
  return minPrice + product.unitPrice.amt;
}

// ─── Discounts availability ──────────────────────────────────────────────────

/** Lookup table of which discount types are allowed per product type. */
export const DISCOUNT_AVAILABILITY = {
  [ProductType.PRINT]: {
    [DiscountType.COUPON]: true,
    [DiscountType.WALLET]: true,
  },
  [ProductType.STORE]: {
    [DiscountType.COUPON]: false,
    [DiscountType.WALLET]: true,
  },
  [ProductType.WALLETBONUS]: {
    [DiscountType.COUPON]: false,
    [DiscountType.WALLET]: false,
  },
  [ProductType.GENERIC]: {
    [DiscountType.COUPON]: false,
    [DiscountType.WALLET]: true,
  },
};

// ─── Product base extraction ─────────────────────────────────────────────────

/**
 * Extracts the variant-relevant pricing and inventory fields from a full product.
 * @param product - The source product to extract fields from.
 * @returns A plain object with the pricing and inventory fields shared between products and their variants.
 */
export const extractProductVariantBase = (product: Product): Pick<ProductBase, 'id' | 'unitPrice' | 'discount' | 'hideDiscount' | 'cost' | 'stock' | 'soldQty' | 'allowSaleNoStock' | 'sku' | 'requiresShipping' | 'weight' | 'pictureUrl' | 'pictureId'> => {
  const {
    id, unitPrice, discount, hideDiscount, cost, stock, soldQty,
    allowSaleNoStock, sku, requiresShipping, weight, pictureUrl, pictureId,
  } = product;
  return {
    id, unitPrice, discount, hideDiscount, cost, stock, soldQty,
    allowSaleNoStock, sku, requiresShipping, weight, pictureUrl, pictureId,
  };
};

// ─── Product Snapshot builder ────────────────────────────────────────────────

/**
 * Creates a lean snapshot of a product for embedding in cart or order items.
 * Strips computed runtime fields and adds type-specific fields where applicable.
 * @param product - The full product to snapshot.
 * @returns A {@link ProductSnapshot} with all `undefined` fields removed.
 */
export const newProductSnapshot = (product: ProductUI): ProductSnapshot => {
  const {
    id, variantId, type, title, description, descriptionLines, unitPrice,
    pictureUrl, pictureId, weight, extra, folderName, listOrder, vendor,
    slug, optionsValue, properties, allowSaleNoStock, stock, sku,
    packProducts, presale, freeShipping, commissionable, discount,
    hideDiscount, cost, soldQty, requiresShipping, _version, mediaFiles,
    seo, featured, categories, labels, status, options, variantCount,
    variants, exclusive, relevance, categoryScore, internalId, copyPrice,
    pictureSecondaryUrl, pictureSecondaryId,
  } = product;

  // NOTE: `variants` (full hydrated array) is intentionally excluded from the
  // cart snapshot. Keeping it would (a) bloat the encrypted localStorage write
  // performed on every refreshCartPrice(), and (b) cause cascading change
  // detection cycles when each per-variant Firestore listener mutates the
  // hydrated variant inside the snapshot. Variants are re-fetched on demand by
  // CartService.setProductUpdates() via getVariant$().
  const base: any = {
    id, variantId, type, title, description, descriptionLines, unitPrice,
    pictureUrl, pictureId, weight, extra, folderName, listOrder, vendor,
    slug, optionsValue, properties, allowSaleNoStock, stock, sku,
    packProducts, presale, freeShipping, commissionable, discount,
    hideDiscount, cost, soldQty, requiresShipping, _version, mediaFiles,
    seo, featured, categories, labels, status, options, variantCount,
    exclusive, relevance, categoryScore, internalId, copyPrice,
    pictureSecondaryUrl, pictureSecondaryId,
  };
  // Suppress unused destructured `variants` (kept in destructure for clarity).
  void variants;

  // Add type-specific fields
  if (product.type === ProductType.PRINT) {
    base.printingGroup = (product as any).printingGroup;
    base.royalty = (product as any).royalty;
  }
  if (product.type === ProductType.STORE) {
    base.subType = (product as any).subType;
    base.customElementsSelected = (product as any).customElementsSelected;
    base.customCombination = (product as any).customCombination;
    base.customType = (product as any).customType;
  }

  // Remove undefined values
  return Object.fromEntries(
    Object.entries(base).filter(([_, v]) => v !== undefined),
  ) as unknown as ProductSnapshot;
};

// ─── UI transformation helpers ───────────────────────────────────────────────

/**
 * Inserts a size suffix before the last file extension in an image filename
 * (e.g. `"img.jpg"` → `"img_m.jpg"`).
 * @param name - The original filename or URL path.
 * @param suffix - The suffix to insert before the extension (e.g. `"m"` for medium).
 * @returns The modified filename with the suffix inserted, or the original name with
 *          the suffix appended when no extension is present.
 */
export const addImageSuffix = (name: string, suffix: string): string => {
  return name?.includes('.') ? name.replace(/\.(?!.*\.)/, `_${suffix}.`) : `${name}_${suffix}`;
};

/**
 * Maps a full product into the {@link ProductUI} model used in product cards and lists.
 * Spreads all product fields and adds pre-computed display fields (price, isSoldOut, thumbnails…).
 * @param product - The source product to transform.
 * @returns A {@link ProductUI} object with pre-computed display fields.
 */
export const toProductListUI = (product: Product): ProductUI => ({
  ...product,
  price: saledPrice(product),
  isSoldOut: isAllProductSoldOut(product),
  isCustom: isCustom(product),
  hasVariant: !!product.variantCount,
  thumbnail: product.pictureUrl ? addImageSuffix(product.pictureUrl, 'm') : '',
  thumbnail2: product.pictureSecondaryUrl ? addImageSuffix(product.pictureSecondaryUrl, 'm') : '',
  cheapestCombinationPrice: cheapestCombinationPrice(product),
} as ProductUI);

/**
 * Maps a print product into the flat {@link DocumentListUI} model used in document lists.
 * @param product - The source print product to transform.
 * @returns A {@link DocumentListUI} object with a pre-computed thumbnail and sale price.
 */
export const toDocumentListUI = (product: Product): DocumentListUI => {
  const { id, title, vendor, pictureUrl, unitPrice, extra, slug, soldQty, status } = product;
  const price = printSaledPrice(product);
  const thumbnail = pictureUrl ? addImageSuffix(pictureUrl, 'm') : '';
  return { id, title, vendor, pictureUrl, unitPrice, extra, slug, soldQty, price, status, thumbnail };
};

// ─── Print pricing helpers ───────────────────────────────────────────────────

/**
 * Looks up the unit price for a given print-settings combination in the pricing table.
 * The lookup key is built by sorting and joining the values of `color`, `pageSize`,
 * `paperWeight`, `twoSided` and `pagesPerSheet`.
 * @param settings - Current print settings containing the key fields.
 * @param tablePrices - Array of {@link PrintVariant} price entries to search.
 * @returns The matching price per sheet in the base currency, or `0` when no match is found.
 */
export const getPriceOption = (settings: PrintSettings, tablePrices: Array<PrintVariant>): number => {
  const options = ['color', 'pageSize', 'paperWeight', 'twoSided', 'pagesPerSheet']
    .map(s => settings[s])
    .sort()
    .join(',');
  const priceOption = tablePrices.find(price => options === price.options.sort().join(','));
  return priceOption?.price ?? 0;
};

/**
 * Removes the 21 % Spanish IVA from a price when tax reduction is required.
 * @param price - The gross price amount to adjust.
 * @param mustReduceTax - When `true` the price is divided by 1.21 to extract the net amount.
 * @returns The net price (IVA excluded) when `mustReduceTax` is `true`; otherwise the original gross price.
 */
export const getAdjustedPrice = (price: number, mustReduceTax: boolean): number => {
  return mustReduceTax ? price / 1.21 : price;
};

/**
 * Calculates the total number of printed sides (cover + body) for a file.
 * @param pages - Total number of document pages.
 * @param settings - Print settings that control pages-per-sheet and duplex behaviour.
 * @param coverType - Cover configuration that determines cover sides and pages-per-sheet.
 * @returns The total number of printed sides required to print the file.
 */
export const getTotalSides = (pages: number, settings: PrintSettings, coverType: CoverTypeConfig): number => {
  const coverSides = coverType.twoSided ? 2 : 1;
  let tmpSides = Math.ceil(
    (pages - coverSides * coverType.pagesPerSheet) / (settings.pagesPerSheet * 1),
  );
  tmpSides += coverSides;
  return tmpSides;
};

/**
 * Converts the total number of printed sides into the physical sheet count a file requires.
 * @param sides - Total printed sides for the file (as returned by {@link getTotalSides}).
 * @param settings - Print settings indicating whether two-sided printing is active.
 * @param coverType - Cover configuration used to determine the number of cover-side sheets.
 * @returns The number of physical sheets needed to print the file.
 */
export const getFileSheets = (sides: number, settings: PrintSettings, coverType: CoverTypeConfig): number => {
  const coverSides = coverType.twoSided ? 2 : 1;
  return (settings.twoSided ? Math.ceil((sides - coverSides) / 2) : sides - coverSides) + 1;
};

/**
 * Computes the total print cost for a single file based on sheet count, settings and extra-price config.
 * Accounts for document colour, cover colour, skip-fit-to-page and blank-sheet surcharges.
 * @param sheets - Total physical sheets for the file.
 * @param settings - Current print settings (colour, paper weight, sided-ness, etc.).
 * @param coverType - Cover configuration (colour, paper weight, pages-per-sheet, two-sided flag).
 * @param blankSheets - Number of blank sheets inserted before/after the document.
 * @param file - The print-file descriptor with per-file flags (cover lamination, colour overrides, etc.).
 * @param tablePrices - Pricing table used for per-sheet rate lookups.
 * @param extraPrices - Extra pricing config for cover-colour, doc-colour and blank-sheet surcharges.
 * @param blankSheetPercentageCalc - Percentage of the base sheet price applied to each blank sheet.
 * @returns The total cost in the base currency for printing this file.
 */
export function getFilePrice(
  sheets: number,
  settings: PrintSettings,
  coverType: CoverTypeConfig,
  blankSheets: number,
  file: _PrintFile,
  tablePrices: Array<PrintVariant>,
  extraPrices: ExtraPrices,
  blankSheetPercentageCalc: number,
): number {
  const isColor = settings[Settings.COLOR] === PrintColor.COLOR;

  if (settings[Settings.DOC_COLOR] && !isColor) {
    settings[Settings.COLOR] = PrintColor.COLOR;
  }
  if (!settings[Settings.DOC_COLOR] && isColor) {
    settings[Settings.COLOR] = PrintColor.BLACKNWHITE;
  }
  if (sheets === 1) {
    settings[Settings.TWO_SIDED] = false;
  }

  const price = getPriceOption(settings, tablePrices);
  const coverSides = coverType.twoSided ? 2 : 1;
  const subTotalDocument = Math.max(0, sheets - coverSides) * price;

  const coverSettings = cloneDeep(settings);
  coverSettings[Settings.COLOR] = coverType.coverColor ? PrintColor.COLOR : PrintColor.BLACKNWHITE;
  coverSettings[Settings.TWO_SIDED] = sheets === 1 ? false : coverType.twoSided;
  coverSettings[Settings.PAGES_PER_SHEET] = coverType.pagesPerSheet;
  coverSettings[Settings.PAPER_WEIGHT] = file.coverLaminated
    ? settings[Settings.PAPER_WEIGHT]
    : coverType.paperWeight;

  const priceCover = getPriceOption(coverSettings, tablePrices) * Math.min(coverSides, sheets);
  let subTotal = subTotalDocument + priceCover;

  if (coverType.coverColor && settings[Settings.COLOR] === PrintColor.BLACKNWHITE) {
    const extraPrice = settings[Settings.COVER_LAMINATED]
      ? 0
      : extraPrices[Settings.COVER_COLOR]?.[String(settings[Settings.COVER_COLOR])]?.price ?? 0;
    subTotal += extraPrice;
  }

  if (settings[Settings.DOC_COLOR] && !isColor && !(settings[Settings.COVER_COLOR] && sheets === 1)) {
    const extraDocColor = sheets * (extraPrices[Settings.DOC_COLOR]?.[String(settings[Settings.DOC_COLOR])]?.price ?? 0);
    subTotal += extraDocColor;
  }

  if (settings[Settings.SKIP_FIT_TO_PAGE]) {
    const extraPrice = sheets * (extraPrices[Settings.SKIP_FIT_TO_PAGE]?.[String(settings[Settings.SKIP_FIT_TO_PAGE])]?.price ?? 0);
    subTotal += extraPrice;
  }

  if (blankSheets) {
    const extraBlankSheets = blankSheets * (getPriceOption(settings, tablePrices) * (blankSheetPercentageCalc / 100));
    subTotal += extraBlankSheets;
  }

  return subTotal;
}

/**
 * Computes the finishing cost, human-readable description and unit count for a printing group.
 * Handles binding (standard, laminated, short-edge), stapling, lamination and perforation.
 * @param fullSheets - Total physical sheets in the printing group.
 * @param settings - Current print settings including the selected finishing type.
 * @param printingGroup - The printing group containing all files and their binding/lamination data.
 * @param file - A specific file descriptor for per-file finishing; `null` for grouped calculation.
 * @param extraPrices - Extra pricing config with per-finishing-type price tables.
 * @param ringColorPrice - Price lookup map for binding-ring colours.
 * @param hardCoverPrice - Price lookup map for hard-cover colours (front and back).
 * @param bindingData - Binding configuration data (sheet limits, ring weights, cover weights).
 * @param defaultPrintSettings - Default print settings used to detect non-default hard-cover selections.
 * @returns An object with `description` (human-readable label), `units` (binding/staple count)
 *          and `price` (total finishing cost in the base currency).
 */
export function getFinishing(
  fullSheets: number,
  settings: PrintSettings,
  printingGroup: PrintingGroup,
  file: _PrintFile | null,
  extraPrices: ExtraPrices,
  ringColorPrice: RingColorPrice,
  hardCoverPrice: HardCoverPrice,
  bindingData: any,
  defaultPrintSettings: PrintSettings,
): { description: string; units: number; price: number; } {
  let option = settings[Settings.FINISHING];
  let description = extraPrices.finishing[option]?.description ?? '';
  const isCoverLaminated = printingGroup.files.some(f => !!f.coverLaminated);

  if ([Finishing.BINDING, Finishing.BINDING_LAMINATED, Finishing.BINDINGX].includes(settings[Settings.FINISHING])) {
    const labels = [description];
    labels.push(settings[Settings.GROUPED] === true || settings[Settings.GROUPED] === undefined ? 'Agrupado' : 'Individual');
    labels.push(`Anillas ${settings[Settings.RING_COLOR]?.name}`);
    if (settings[Settings.FINISHING] === Finishing.BINDING) {
      if (settings[Settings.HARD_COVER_FRONT]) {
        labels.push(isCoverLaminated ? 'Portada plastificada' : `T. Delantera ${settings[Settings.HARD_COVER_FRONT].name}`);
      }
      if (settings[Settings.HARD_COVER_BACK]) {
        labels.push(`T. Trasera ${settings[Settings.HARD_COVER_BACK].name}`);
      }
    } else if (settings[Settings.FINISHING] === Finishing.BINDING_LAMINATED) {
      labels.push(`Plastificado: ${settings[Settings.LAMINATION_TYPE]}, ${settings[Settings.LAMINATION_WEIGHT]}`);
    }
    description = labels.join(', ');
  }

  let units = Math.ceil(extraPrices.finishing[option]?.limit ? fullSheets / extraPrices.finishing[option].limit : 1);
  if (settings[Settings.FINISHING] === Finishing.BINDING) {
    units = Math.ceil(fullSheets / bindingData.sheetLimit[settings.paperWeight]);
  } else if (settings[Settings.FINISHING] === Finishing.BINDING_LAMINATED) {
    units = Math.ceil(fullSheets / bindingData.sheetLimitLamination[settings.laminationWeight]);
  }

  const ringColorId = settings['grouped'] ? settings['ringColor']?.id : file?.['ringColor']?.id ?? null;
  let ringPrice = 0;
  let hardCoverFrontPrice = 0;
  let hardCoverBackPrice = 0;
  let laminationPrice = 0;
  let coverLaminatedPrice = 0;

  const getHardCoverColorPrice = (side: Settings.HARD_COVER_FRONT | Settings.HARD_COVER_BACK): number => {
    let price = hardCoverPrice.default;
    let hardCoverColorId: string;
    let exclusive: boolean;
    if (settings[Settings.GROUPED]) {
      hardCoverColorId = settings[side]?.id ?? null;
      exclusive = settings[side]?.exclusive ?? false;
    } else {
      hardCoverColorId = file?.[side]?.id ?? null;
      exclusive = file?.[side]?.exclusive ?? false;
    }
    if (hardCoverColorId && defaultPrintSettings?.[side]?.id !== hardCoverColorId) {
      price = hardCoverPrice.hasOwnProperty(hardCoverColorId)
        ? hardCoverPrice[hardCoverColorId]
        : exclusive ? hardCoverPrice.exclusive : hardCoverPrice.noDefault;
    }
    return price;
  };

  const getLaminationPrice = (): number => {
    if (file) {
      if (settings[Settings.FINISHING] === Finishing.BINDING_LAMINATED) {
        return (extraPrices.finishing[Finishing.LAMINATED].variants.find(
          el => el.options.laminationType === file.laminationType
            && el.options.laminationWeight === file.laminationWeight
            && el.options.pageSize === settings[Settings.PAGE_SIZE],
        )?.price ?? 0) * file.printSheets;
      }
      return (extraPrices.finishing[Finishing.LAMINATED].variants.find(
        el => el.options.laminationType === file.laminationType
          && el.options.laminationWeight === file.laminationWeight
          && el.options.pageSize === settings[Settings.PAGE_SIZE],
      )?.price ?? 0) * units;
    }
    let price = 0;
    printingGroup.files.forEach(f => {
      const _laminationType = settings[Settings.FINISHING] === Finishing.BINDING_LAMINATED
        ? settings[Settings.LAMINATION_TYPE] : f.laminationType;
      const _laminationWeight = settings[Settings.FINISHING] === Finishing.BINDING_LAMINATED
        ? settings[Settings.LAMINATION_WEIGHT] : f.laminationWeight;
      let _units = Math.ceil(extraPrices.finishing[option]?.limit ? f.printSheets / extraPrices.finishing[option].limit : 1);
      if (settings[Settings.FINISHING] === Finishing.BINDING_LAMINATED) {
        _units = f.printSheets;
      }
      price += (extraPrices.finishing[Finishing.LAMINATED].variants.find(
        el => el.options.laminationType === _laminationType
          && el.options.laminationWeight === _laminationWeight
          && el.options.pageSize === settings[Settings.PAGE_SIZE],
      )?.price ?? 0) * _units;
    });
    return price;
  };

  const getCoverLaminatedPrice = (): number => {
    const biggerFormat = {
      [PageSize.A3]: PageSize.A3,
      [PageSize.A4]: PageSize.A3,
      [PageSize.A5]: PageSize.A4,
    };
    const _file = file ?? printingGroup.files.find(f => f.coverLaminated);
    const laminationType = _file?.laminationType ?? settings[Settings.LAMINATION_TYPE] ?? LaminationType.Gloss;
    const laminationWeight = _file?.laminationWeight ?? settings[Settings.LAMINATION_WEIGHT] ?? LaminationWeight.W125;
    return (extraPrices[Settings.COVER_LAMINATED]?.[settings[Settings.PAGE_SIZE]]?.price ?? 0) +
      (extraPrices.finishing[Finishing.LAMINATED].variants.find(
        el => el.options.laminationType === laminationType
          && el.options.laminationWeight === laminationWeight
          && el.options.pageSize === biggerFormat[settings[Settings.PAGE_SIZE]],
      )?.price ?? 0);
  };

  if ([Finishing.BINDING, Finishing.BINDING_LAMINATED].includes(option)) {
    ringPrice = ringColorPrice.hasOwnProperty(ringColorId) ? ringColorPrice[ringColorId] : ringColorPrice.Default;
    if (option === Finishing.BINDING && settings['pageSize'] !== PageSize.A3) {
      hardCoverFrontPrice = isCoverLaminated ? 0 : getHardCoverColorPrice(Settings.HARD_COVER_FRONT);
      hardCoverBackPrice = getHardCoverColorPrice(Settings.HARD_COVER_BACK);
    }
  }

  if (option === Finishing.LAMINATED || option === Finishing.BINDING_LAMINATED) {
    laminationPrice = getLaminationPrice();
  }
  if (option === Finishing.BINDING_LAMINATED) {
    option = Finishing.BINDING;
  }

  if (isCoverLaminated) {
    coverLaminatedPrice = getCoverLaminatedPrice();
  }

  let totalPrice = (((extraPrices.finishing[option]?.variants ? 0 : extraPrices.finishing[option]?.price ?? 0)
    + ringPrice + hardCoverFrontPrice + hardCoverBackPrice) * units)
    + laminationPrice + coverLaminatedPrice;

  if (extraPrices.finishing[option]?.minPrice && extraPrices.finishing[option].minPrice > totalPrice) {
    totalPrice = extraPrices.finishing[option].minPrice;
  }

  return { description, units, price: totalPrice };
}

// ─── Weight helpers ──────────────────────────────────────────────────────────

/**
 * Calculates the total paper weight (in grams) for all files in a printing group.
 * Applies the per-file cover paper weight and the group-wide body paper weight.
 * @param printingGroup - The printing group whose files are totalled.
 * @param weights - {@link WeightMatrix} mapping page-size × paper-weight to grams per sheet.
 * @returns The combined paper weight in grams for the entire printing group.
 */
export const getPrintingGroupSheetsWeight = (
  printingGroup: PrintingGroup,
  weights: WeightMatrix,
): number => {
  let weight = 0;
  if (!printingGroup.printSettings.paperWeight) {
    printingGroup.printSettings.paperWeight = PaperWeight.W80;
  }
  for (const file of printingGroup.files) {
    const coverWeight = weights[printingGroup.printSettings.pageSize]?.[file.coverType?.paperWeight] ?? 0;
    const documentWeight = (weights[printingGroup.printSettings.pageSize]?.[printingGroup.printSettings.paperWeight] ?? 0) * (file.printSheets - 1);
    weight += coverWeight + documentWeight;
  }
  return weight;
};

/**
 * Returns the total weight (in grams) added by a finishing option (binding rings, lamination, etc.).
 * @param finishing - The finishing type identifier (e.g. `Finishing.LAMINATED`).
 * @param product - The print product snapshot whose `printingGroup` data is used for the calculation.
 * @param extraPrices - Extra pricing config providing per-finishing-type weight tables.
 * @param useExtra - When `true` (default) multiplies the unit weight by total print sheets;
 *                   when `false` uses the pre-computed `extra.units` from the product.
 * @returns The total finishing weight in grams.
 */
export const getFinishingWeight = (
  finishing: string,
  product: ProductSnapshot | ProductPrint,
  extraPrices: ExtraPrices,
  useExtra = true,
): number => {
  if (finishing !== Finishing.LAMINATED) {
    if (useExtra) {
      return (extraPrices.finishing[finishing]?.weight ?? 0) * product.printingGroup.printSheets;
    }
    return ((product.extra?.units ?? 1)) * (extraPrices.finishing[finishing]?.weight ?? 0);
  }
  let _weight = 0;
  product.printingGroup.files.forEach(file => {
    _weight += (extraPrices.finishing[finishing]?.variants?.find(
      el => el.options.laminationType === file.laminationType
        && el.options.laminationWeight === file.laminationWeight
        && el.options.pageSize === product.printingGroup.printSettings[Settings.PAGE_SIZE],
    )?.weight ?? 0) * file.printSheets;
  });
  return _weight;
};

// ─── Binding helpers ─────────────────────────────────────────────────────────

/** Result of a binding-ring lookup: chosen diameter, quantity, split flag and weight. */
export interface BindingRingResult {
  diameter: number;
  qty: number;
  split: boolean;
  weight: number;
}

/**
 * Determines the optimal binding-ring diameter, quantity, split flag and weight for a given sheet count.
 * Supports both standard binding and laminated-cover binding.
 * @param bindingData - Raw binding configuration data (sheet limits, ring weight table, cover weight table).
 * @param sheetCount - Number of physical sheets to bind (default `0`).
 * @param pageSize - Paper size of the document (default {@link PageSize.A4}).
 * @param paperWeight - Paper weight used to look up the sheet limit (default {@link PaperWeight.W80}).
 * @param pageTurn - Page-turn edge used to look up ring weight (default {@link PageTurn.LARGESIDE}).
 * @param extraPrices - Extra pricing config needed when the file has a laminated cover; can be `null`.
 * @param file - File descriptor needed for laminated-cover weight calculations; can be `null`.
 * @param settings - Current print settings used to detect laminated-binding mode; can be `null`.
 * @returns A {@link BindingRingResult} with the chosen ring `diameter`, binding `qty`, `split` flag
 *          and total ring + cover `weight` in grams.
 */
export function findBindingRing(
  bindingData: any,
  sheetCount = 0,
  pageSize: PageSize = PageSize.A4,
  paperWeight: PaperWeight = PaperWeight.W80,
  pageTurn: PageTurn = PageTurn.LARGESIDE,
  extraPrices: ExtraPrices = null,
  file: _PrintFile = null,
  settings: PrintSettings = null,
): BindingRingResult {
  const isBindingLaminated = settings && settings.finishing === Finishing.BINDING_LAMINATED;
  const _laminationWeight = isBindingLaminated && file ? file.laminationWeight : settings?.laminationWeight;
  const sheetLimit = isBindingLaminated
    ? bindingData.sheetLimitLamination[_laminationWeight]
    : bindingData.sheetLimit[paperWeight];

  const qty = Math.ceil(sheetCount / sheetLimit);
  const split = qty > 1;
  const _sheetCount = qty > 1 ? Math.ceil(sheetCount / qty) : sheetCount;
  let diameter = 0;
  let weight = 0;

  const ringData = !isBindingLaminated
    ? bindingData.ringWeight.find(e =>
      e.paperWeight[paperWeight][0] < _sheetCount && _sheetCount <= e.paperWeight[paperWeight][1])
    : bindingData.ringWeight.find(e =>
      e.laminationWeight[_laminationWeight][0] < _sheetCount && _sheetCount <= e.laminationWeight[_laminationWeight][1]);

  if (ringData) {
    diameter = ringData.diameter;
  }

  const coverData = bindingData.coverWeight.find(e =>
    e.diameterRange[0] < diameter && diameter <= e.diameterRange[1]);

  if (ringData && coverData) {
    let coverWeight = coverData.pageSize[pageSize];
    if (file && file.coverLaminated) {
      const coverLaminatedWeight = extraPrices.finishing[Finishing.LAMINATED].variants.find(
        el => el.options.laminationType === file.laminationType
          && el.options.laminationWeight === file.laminationWeight
          && el.options.pageSize === pageSize,
      ).weight;
      coverWeight = (coverWeight / 2) + coverLaminatedWeight;
    }
    const _weight = ringData.pageSize[pageSize][pageTurn] + (isBindingLaminated ? 0 : coverWeight);
    weight = _weight * qty;
  }

  if (isBindingLaminated && file) {
    const laminationWeight = extraPrices.finishing[Finishing.LAMINATED].variants.find(
      el => el.options.laminationType === file.laminationType
        && el.options.laminationWeight === _laminationWeight
        && el.options.pageSize === pageSize,
    ).weight * sheetCount;
    weight += laminationWeight;
  }

  return { diameter, qty, split, weight };
}

// ─── Vendor helpers ──────────────────────────────────────────────────────────

/**
 * Creates a {@link VendorRef} from a user-like object.
 * @param user - Object containing the vendor's `id`, `displayName` and `photoURL`.
 * @returns A {@link VendorRef} with the fields mapped to `id`, `name` and `thumbnail`.
 */
export const newVendorRef = (user: { id?: string; displayName: string; photoURL: string; }): VendorRef => {
  const { id, displayName, photoURL } = user;
  return { id, name: displayName, thumbnail: photoURL };
};

// ─── Print product price helpers ─────────────────────────────────────────────

/**
 * Returns the sum of the base unit price and the extra finishing price for a print product.
 * @param printProduct - The print product whose prices are summed.
 * @returns The combined amount (base + extras) in the product base currency units.
 */
export const getTotalUnitPrice = (printProduct: Product): number => {
  return printProduct.unitPrice.amt + (printProduct.extra?.price ?? 0);
};

/**
 * Calculates the total price for a print-type cart item.
 * @param unitPriceAmt - Per-copy unit price (base + extras) in the base currency.
 * @param qty - Number of copies ordered.
 * @param royalty - One-time creator royalty fee added once regardless of quantity.
 * @returns Total price: `unitPriceAmt × qty + royalty`.
 */
export const calcPrintItemTotal = (unitPriceAmt: number, qty: number, royalty: number): number => {
  return unitPriceAmt * qty + royalty;
};

// ─── Variant UI helpers ──────────────────────────────────────────────────────

/**
 * Picks the best variant to display by default using the following priority:
 * 1. Keep the currently selected variant if it is still visible.
 * 2. First available (not sold-out) visible variant.
 * 3. First visible variant regardless of stock.
 * @param variants - Array of all product variants (visible and hidden).
 * @param currentSelected - The variant currently selected, if any.
 * @returns The best default {@link ProductVariant}, or `null` when no visible variant exists.
 */
export const selectDefaultVariant = (
  variants: Array<ProductVariant>,
  currentSelected?: ProductVariant,
): ProductVariant | null => {
  if (currentSelected && currentSelected.visible !== false) return currentSelected;
  const available = variants.find(v => !isSoldOut(v) && v.visible !== false);
  if (available) return available;
  return variants.find(v => v.visible !== false) ?? null;
};

/**
 * Builds the union of all available values for each option dimension across all variants.
 * @param variants - Array of product variants to inspect.
 * @param optionCount - Number of option dimensions (e.g. `2` for size + colour).
 * @returns A nested array where each element lists the unique values for that option index.
 */
export const getAvailableOptionValues = (
  variants: Array<ProductVariant>,
  optionCount: number,
): Array<Array<string>> => {
  return Array.from({ length: optionCount }, (_, i) =>
    variants.reduce((acc: string[], variant) => {
      const val = variant.optionsValue[i];
      return acc.includes(val) ? acc : [...acc, val];
    }, []),
  );
};

// ─── Cart-item sorting ────────────────────────────────────────────────────────

/**
 * Sorts cart or order items so that PRINT products come first, STORE products second,
 * and all other types last. The original array is not mutated.
 * @param items - Array of items with a `product.type` field.
 * @returns A new sorted array with the same items in display-priority order.
 */
export const sortItemsByProductType = <T extends { product: { type: string; }; }>(items: Array<T>): Array<T> => {
  const rank = (type: string): number =>
    type === ProductType.PRINT ? 0 : type === ProductType.STORE ? 1 : 2;
  return [...items].sort((a, b) => rank(a.product.type) - rank(b.product.type));
};

// ─── Finishing helpers ───────────────────────────────────────────────────────

/**
 * Returns `true` when the finishing type is a binding variant
 * (BINDING, BINDINGX, or BINDING_LAMINATED).
 */
export function isBindingFinishing(finishing: Finishing): boolean {
  return [Finishing.BINDING, Finishing.BINDINGX, Finishing.BINDING_LAMINATED].includes(finishing);
}

/**
 * Returns `true` when the finishing type involves lamination
 * (LAMINATED or BINDING_LAMINATED).
 */
export function isLaminationFinishing(finishing: Finishing): boolean {
  return [Finishing.LAMINATED, Finishing.BINDING_LAMINATED].includes(finishing);
}

// ─── Cover-type helpers ──────────────────────────────────────────────────────

/**
 * Builds a CoverType ID string from the component parts.
 * Example: `'Color2Side1Page'`, `'Black1Side4Page'`
 */
export function buildCoverTypeId(
  coverColor: boolean,
  twoSided: boolean,
  pagesPerSheet: PagesPerSheet,
): string {
  const colorPart = coverColor ? 'Color' : 'Black';
  const sidePart = twoSided ? '2Side' : '1Side';
  const pagePart = `${pagesPerSheet}Page`;
  return `${colorPart}${sidePart}${pagePart}`;
}

/**
 * Calculates the default CoverType for a file based on its individual settings
 * and the group print settings.
 */
export function getDefaultCoverType(
  file: _PrintFile,
  printSettings: PrintSettings,
): CoverTypeConfig {
  const isCoverLaminated = file.coverLaminated || (printSettings[Settings.COVER_LAMINATED] ?? false);
  const coverColor = file.coverColor || file.docColor || isCoverLaminated;
  const twoSided = isCoverLaminated ? false : printSettings[Settings.TWO_SIDED];
  const pagesPerSheet = isCoverLaminated ? PagesPerSheet.ONE : printSettings[Settings.PAGES_PER_SHEET];
  const paperWeight = file.coverType?.paperWeightChanged
    ?? (isCoverLaminated ? printSettings[Settings.PAPER_WEIGHT] : printSettings[Settings.PAPER_WEIGHT]);

  const id = buildCoverTypeId(coverColor, twoSided, pagesPerSheet);

  return {
    id,
    coverColor,
    twoSided,
    pagesPerSheet,
    paperWeight,
    default: true,
  };
}

// ─── File color checks ───────────────────────────────────────────────────────

/** Returns `true` when at least one file in the list has `docColor === false`. */
export function hasBWFiles(files: Pick<_PrintFile, 'docColor'>[]): boolean {
  return files.some(f => !f.docColor);
}

/** Returns `true` when at least one file in the list has `docColor` or `coverColor` set. */
export function hasColorFiles(files: Pick<_PrintFile, 'docColor' | 'coverColor'>[]): boolean {
  return files.some(f => f.docColor || f.coverColor);
}

// ─── Binding data helpers ────────────────────────────────────────────────────

import type { HardCover, RingColor } from './product.models';

export const RING_CATEGORY_LABELS: Record<string, string> = {
  standard: 'Colores estándar',
  pastel: 'Colores pastel',
  nature: 'Colores nature',
  glitter: 'Anillas con PURPURINA',
};

export const COVER_CATEGORY_LABELS: Record<string, string> = {
  standard: 'Colores estándar',
  pastel: 'Colores pastel',
  nature: 'Colores nature',
};

export const BINDING_CATEGORY_ORDER = ['standard', 'pastel', 'nature', 'glitter'];

export interface BindingCategoryGroup {
  category: string;
  label: string;
  items: (RingColor | HardCover)[];
}

/**
 * Groups ring colors or hard covers by their `category` field.
 */
export function groupBindingByCategory(
  items: (RingColor | HardCover)[],
  labels: Record<string, string>,
): BindingCategoryGroup[] {
  const grouped = new Map<string, (RingColor | HardCover)[]>();
  for (const item of items) {
    const cat = (item as RingColor).category ?? 'standard';
    if (!grouped.has(cat)) grouped.set(cat, []);
    grouped.get(cat)!.push(item);
  }
  return BINDING_CATEGORY_ORDER
    .filter(cat => grouped.has(cat))
    .map(cat => ({
      category: cat,
      label: labels[cat] ?? cat,
      items: grouped.get(cat)!,
    }));
}

/** Returns `true` when the color hex represents a transparent/white cover. */
export function isTransparentCover(color: string): boolean {
  return color === '#FFFFFF' || color?.toLowerCase() === '#ffffff';
}

